import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  BedDouble,
  Bath,
  Fan,
  Snowflake,
  Wifi,
  Shirt,
  Sun,
  Building2,
  Check,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { ROOMS_META, CONTACT } from "../data/site";
import { api } from "../lib/adminApi";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.003 3C9.373 3 3.99 8.383 3.99 15.013c0 2.117.553 4.184 1.605 6.008L3.9 28.1l7.24-1.897a12.02 12.02 0 0 0 4.86 1.02h.004c6.63 0 12.013-5.383 12.013-12.013C28.017 8.383 22.633 3 16.003 3zm0 21.99h-.003a9.96 9.96 0 0 1-4.548-1.11l-.326-.194-4.297 1.127 1.147-4.19-.212-.335a9.93 9.93 0 0 1-1.523-5.27c0-5.514 4.487-10 10.006-10 5.517 0 10 4.486 10 10.001 0 5.514-4.483 9.97-10 9.97zm5.494-7.463c-.3-.15-1.78-.878-2.056-.978-.276-.1-.477-.15-.678.15-.2.3-.777.978-.953 1.178-.175.2-.35.226-.65.075-.3-.15-1.27-.468-2.42-1.492-.894-.797-1.498-1.782-1.673-2.082-.175-.3-.019-.462.132-.611.135-.135.3-.35.45-.526.15-.175.2-.3.3-.5.1-.2.05-.376-.025-.526-.075-.15-.677-1.632-.928-2.235-.244-.586-.492-.507-.677-.516l-.577-.01c-.2 0-.526.075-.802.376s-1.053 1.028-1.053 2.51 1.078 2.912 1.228 3.112c.15.2 2.122 3.24 5.14 4.544.718.31 1.278.494 1.715.632.72.229 1.376.196 1.894.119.578-.086 1.78-.727 2.03-1.43.25-.703.25-1.305.175-1.43-.075-.126-.276-.2-.576-.35z" />
  </svg>
);

function amenityIcon(text) {
  const s = text.toLowerCase();
  if (s.includes("wi-fi") || s.includes("wifi")) return Wifi;
  if (s.includes("air") || s.includes("aire") || s.includes("clim") || s.includes("klima") || s.includes("condizion") || s.includes("condicion") || s.includes("空调") || s.includes("кондиц")) return Snowflake;
  if (s.includes("fan") || s.includes("ventil") || s.includes("风扇") || s.includes("вентил")) return Fan;
  if (s.includes("bath") || s.includes("baño") || s.includes("bagno") || s.includes("bad") || s.includes("salle") || s.includes("banheiro") || s.includes("卫浴") || s.includes("ванн")) return Bath;
  if (s.includes("balcon") || s.includes("balcón") || s.includes("balkon") || s.includes("阳台") || s.includes("varanda") || s.includes("балкон")) return Sun;
  if (s.includes("floor") || s.includes("piso") || s.includes("étage") || s.includes("etage") || s.includes("stock") || s.includes("этаж") || s.includes("楼") || s.includes("piano") || s.includes("andar")) return Building2;
  if (s.includes("wardrobe") || s.includes("ropero") || s.includes("armadi") || s.includes("armari") || s.includes("armoire") || s.includes("schrank") || s.includes("rack") || s.includes("perch") || s.includes("cabid") || s.includes("guarda") || s.includes("衣柜") || s.includes("挂衣") || s.includes("шкаф") || s.includes("вешал")) return Shirt;
  return BedDouble;
}

const RoomImages = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const many = images.length > 1;

  // Start/stop auto-sliding based on whether the card is scrolled into view
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!many || !inView) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(id);
  }, [many, inView, images.length]);

  const go = (d) => (e) => {
    e.stopPropagation();
    setIdx((i) => (i + d + images.length) % images.length);
  };
  return (
    <div ref={ref} className="absolute inset-0">
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={alt}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      {many && (
        <>
          <button
            onClick={go(-1)}
            data-testid="room-img-prev"
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-purpleink shadow-md transition-colors hover:bg-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={go(1)}
            data-testid="room-img-next"
            aria-label="Next photo"
            className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-purpleink shadow-md transition-colors hover:bg-white"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/60"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const Rooms = () => {
  const { t } = useLang();
  const { openBooking } = useBooking();
  const rooms = t.rooms.list;
  const [liveAvail, setLiveAvail] = useState(null);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.getPublicAvailability().then(setLiveAvail).catch(() => {});
    api.getPricing().then(setPricing).catch(() => {});
  }, []);

  const waRoom = (roomName) => {
    const b = t.book;
    const text = encodeURIComponent(`${b.wa_intro}\n${b.wa_room}: ${roomName}`);
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${text}`, "_blank");
  };

  return (
    <section id="rooms" className="bg-white py-24 sm:py-32" data-testid="rooms-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <span className="text-xs font-medium uppercase tracking-[0.3em] text-teal">
            {t.rooms.kicker}
          </span>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-purpleink-deep sm:text-5xl">
            {t.rooms.title}
          </h2>
          <p className="mt-4 text-lg text-stone-600">{t.rooms.sub}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {rooms
            .map((room, i) => ({ room, meta: ROOMS_META[i] }))
            .filter((x) => x.meta && !x.meta.hidden)
            .sort((a, b) => (a.meta.order ?? 99) - (b.meta.order ?? 99))
            .map(({ room, meta }, idx) => {
            const available =
              liveAvail && liveAvail[meta.id] !== undefined
                ? liveAvail[meta.id]
                : meta.available ?? meta.count;
            const soldOut = available <= 0;
            const availLabel = soldOut
              ? t.rooms.soldOut
              : `${available} ${available === 1 ? t.rooms.roomLabel.one : t.rooms.roomLabel.other}`;
            const availClass = soldOut
              ? "bg-stone-400 text-white"
              : available >= 2
              ? "bg-teal text-white"
              : "bg-marigold text-purpleink-deep";
            return (
              <motion.article
                key={meta.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (idx % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative flex flex-col overflow-hidden rounded-[1.75rem] border border-stone-200 bg-sand ${meta.span}`}
                data-testid={`room-card-${meta.id}`}
              >
                <div className="relative h-64 overflow-hidden sm:h-72">
                  <RoomImages images={meta.images} alt={room.name} />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute left-4 top-4 z-10 flex gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${availClass}`}
                      data-testid={`room-availability-${meta.id}`}
                    >
                      {!soldOut && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                      {availLabel}
                    </span>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-2xl font-semibold text-purpleink-deep">
                      {room.name}
                    </h3>
                    <div className="flex-shrink-0 text-right" data-testid={`room-price-${meta.id}`}>
                      <span className="font-display text-2xl font-bold leading-none text-teal-deep">
                        ${pricing?.base?.[meta.id] ?? meta.price}
                      </span>
                      <span className="mt-0.5 block text-xs font-medium text-stone-500">
                        {t.rooms.perNight}
                      </span>
                      {pricing?.rate ? (
                        <span className="mt-0.5 block text-[11px] font-medium text-stone-400">
                          C${Math.round((pricing.base?.[meta.id] ?? meta.price) * pricing.rate).toLocaleString()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">
                    {room.desc}
                  </p>

                  <ul className="mt-6 flex flex-wrap gap-2.5">
                    {room.amenities.map((a, ai) => {
                      const Icon = amenityIcon(a);
                      return (
                        <li
                          key={ai}
                          className="flex items-center gap-1.5 rounded-full border border-teal/25 bg-teal/5 px-3 py-1.5 text-xs font-medium text-teal-deep"
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {a}
                        </li>
                      );
                    })}
                  </ul>

                  {t.rooms.extras && (
                    <div className="mt-4" data-testid={`room-included-${meta.id}`}>
                      <span className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-[#B8760A]">
                        {t.rooms.includedLabel}
                      </span>
                      <ul className="mt-2 flex flex-wrap gap-2.5">
                        {t.rooms.extras.map((a, ai) => (
                          <li
                            key={ai}
                            className="flex items-center gap-1.5 rounded-full border border-marigold/40 bg-marigold/10 px-3 py-1.5 text-xs font-medium text-purpleink-deep"
                          >
                            <Check className="h-3.5 w-3.5 text-[#B8760A]" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
                    <button
                      onClick={() => openBooking(room.name)}
                      data-testid={`room-book-${meta.id}`}
                      className="group/btn flex w-fit items-center gap-2 rounded-full bg-purpleink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-purpleink-deep active:scale-95"
                    >
                      {t.rooms.book}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </button>
                    <button
                      onClick={() => waRoom(room.name)}
                      data-testid={`room-whatsapp-${meta.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#128C7E] transition-colors hover:text-[#25D366]"
                    >
                      <WhatsAppIcon className="h-4 w-4" />
                      {t.rooms.whatsapp}
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
